import { FC, useEffect, useState } from "react"
import { FormContainer, useForm, useWatch } from "react-hook-form-mui"
import { IFormProps } from "@/core/interfaces"
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material"
import { Modal, SnackbarAlert } from "@/core/components"
import { TgMailing } from "../../../interfaces/mailing"
import { usePagedEnqueueRun } from "../usePagedEnqueueRun"
import { buildPagedRequest } from "./buildRecipients"
import { MailingBody } from "./mailingBody"
import { MailingCredentials } from "./mailingCredentials"
import { clearMailingPrefill, getTelegramState, setUsers } from "../../../store"
import { MethodSelectField } from "../methodSelectField"
import { Name } from "../name"
import { ScheduleDate } from "../scheduleDate"
import { Speed } from "../speed"
import { Comment } from "../comment"
import { Type } from "../type"
import { MailingCredentialsFilters } from "./mailingCredentialsTable"
import { UserFiltersForm } from "./userFilters"
import { DedupByUserId } from "../dedupByUserId"
import { useAppDispatch, useAppSelector } from "@/modules/app/hooks"
import { PreviewFab } from "../../TelegramPreview"
import { getLaunchWarnings, hasActiveFilters } from "./recipientGuards"

interface IProps extends IFormProps<void> {}
export interface IMailingFormData<
  M extends TgMailing.AvailableMethods = TgMailing.AvailableMethods,
> extends Omit<TgMailing.CreateMailingData<M>, "body" | "token" | "messages"> {
  body: Omit<
    TgMailing.CreateMailingData<M>["messages"][number]["body"],
    "chat_id"
  >
  credentials: string
  method: TgMailing.AvailableMethods
}

export const MailingForm: FC<IProps> = (props) => {
  const {
    phase,
    enqueued,
    errorText,
    mailingLive,
    resubmitBlocked,
    cancelErrorId,
    run,
    retry,
    stop,
    clearCancelError,
  } = usePagedEnqueueRun()
  const isBusy = phase === "uploading" || phase === "paused"

  const [formError, setFormError] = useState<string | null>(null)
  const [finishConfirmOpen, setFinishConfirmOpen] = useState(false)
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false)

  const dispatch = useAppDispatch()
  const { mailingPrefill } = useAppSelector(getTelegramState)

  const formContext = useForm<IMailingFormData>({
    defaultValues: {
      deduplicateByUser: true,
      ...(mailingPrefill ?? {}),
    },
  })

  // Recipient filters live only in this form (not redux), so they are not
  // persisted to localStorage and reset on every page reload.
  const filtersFormContext = useForm<UserFiltersForm>({ defaultValues: {} })
  const filterValues = useWatch({
    control: filtersFormContext.control,
  }) as UserFiltersForm

  // One-time consumption of prefill + reset of stale persisted recipients so the
  // form never starts with recipients carried over from a past session.
  useEffect(() => {
    if (mailingPrefill) dispatch(clearMailingPrefill())
    dispatch(setUsers([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (phase === "success") {
      formContext.reset()
      filtersFormContext.reset({})
      dispatch(setUsers([]))
      setSuccessSnackbarOpen(true)
    }
  }, [phase, formContext, filtersFormContext, dispatch])

  const method = formContext.watch("method")
  const body = formContext.watch("body")
  const credentials = formContext.watch("credentials")
  const scheduleDate = formContext.watch("scheduleDate")
  const recipientCount = (credentials ?? "")
    .split(/[\s,;]+/g)
    .filter(Boolean).length

  /**
   * Validation to run before opening the preview dialog.
   * Triggers RHF validation on the text / caption field so errors are shown
   * in-place; the dialog opens only if the field is valid.
   */
  const handlePreviewBeforeOpen = async (): Promise<boolean> => {
    if (method === "sendMessage") {
      return formContext.trigger("body.text" as any)
    }
    if (
      method === "sendPhoto" ||
      method === "sendVideo" ||
      method === "sendAnimation"
    ) {
      return formContext.trigger("body.caption" as any)
    }
    return true
  }

  const [expanded, setExpanded] = useState(false)
  const [pendingData, setPendingData] = useState<IMailingFormData | null>(null)
  const [warnings, setWarnings] = useState<string[]>([])

  const performSubmit = (data: IMailingFormData) => {
    try {
      setFormError(null)
      const { request, recipients, useMtProto } = buildPagedRequest(data)
      run({ request, recipients, useMtProto })
    } catch (e) {
      setFormError(e instanceof Error ? e.message : String(e))
    }
  }

  const onSubmit = (data: IMailingFormData) => {
    const launchWarnings = getLaunchWarnings({
      hasFilters: hasActiveFilters(filterValues as Record<string, unknown>),
      // The recipient list is whatever is in `credentials`; treat any explicit,
      // operator-entered list as an intentional audience. The "no filters"
      // warning is about launching against the full base via the selector.
      allUsersOptIn: false,
      recipientCount,
    })
    if (launchWarnings.length > 0) {
      setWarnings(launchWarnings)
      setPendingData(data)
      return
    }
    performSubmit(data)
  }

  return (
    <Stack spacing={1}>
      <Box mb={3}>
        <Box mb={1} sx={{ display: "flex", flexDirection: "row-reverse" }}>
          <Button onClick={() => setExpanded(!expanded)}>
            {expanded ? "Закрыть" : "Выбрать пользователей"}
          </Button>
        </Box>
        <Collapse in={expanded}>
          <Typography variant="h5">
            Выбрать пользователей для рассылки
          </Typography>
          <MailingCredentialsFilters filtersFormContext={filtersFormContext} />
        </Collapse>
      </Box>
      <FormContainer<IMailingFormData>
        onSuccess={onSubmit}
        formContext={formContext}
      >
        <SnackbarAlert
          isOpen={successSnackbarOpen}
          onClose={() => setSuccessSnackbarOpen(false)}
          variant="success"
          title="Успешно"
          message="Рассылка создана, отправка идёт"
        />
        <SnackbarAlert
          isOpen={!!formError}
          onClose={() => setFormError(null)}
          variant="error"
          title="Произошла ошибка"
          message={formError ?? ""}
        />
        <SnackbarAlert
          isOpen={!!cancelErrorId}
          onClose={clearCancelError}
          variant="error"
          message={`Не удалось остановить рассылку. Отмените её вручную из списка активных (id: ${cancelErrorId}).`}
        />
        {phase === "uploading" && (
          <Alert severity="info" icon={<CircularProgress size={16} />}>
            Загрузка получателей: {enqueued.toLocaleString("ru-RU")}.{" "}
            {scheduleDate
              ? "Отправка начнётся по расписанию; загрузка идёт уже сейчас."
              : "Отправка уже идёт во время загрузки — часть аудитории задействована до завершения сборки."}
          </Alert>
        )}
        {phase === "paused" && (
          <Alert severity="warning">
            {errorText}
            <Box mt={1} display="flex" gap={1}>
              <Button size="small" variant="contained" onClick={retry}>
                Повторить
              </Button>
              <Button
                size="small"
                color="warning"
                onClick={() => setFinishConfirmOpen(true)}
              >
                Остановить и сохранить отчёт
              </Button>
              <Button
                size="small"
                color="error"
                onClick={() => void stop(false)}
              >
                Удалить рассылку
              </Button>
            </Box>
          </Alert>
        )}
        {finishConfirmOpen && (
          <Modal
            disableBackdropClose
            title="Остановить и сохранить отчёт?"
            onClose={() => setFinishConfirmOpen(false)}
            element={
              <Box>
                <Typography>
                  Часть аудитории уже получила сообщения — отправка идёт с
                  первой загруженной страницы. Рассылка будет остановлена, отчёт
                  сохранится как частичный.
                </Typography>
                <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
                  <Button onClick={() => setFinishConfirmOpen(false)}>
                    Продолжить загрузку
                  </Button>
                  <Button
                    color="warning"
                    variant="contained"
                    onClick={() => {
                      setFinishConfirmOpen(false)
                      void stop(true)
                    }}
                  >
                    Остановить
                  </Button>
                </Box>
              </Box>
            }
          />
        )}
        {phase === "failed" && (
          <Alert severity="error">
            {errorText}
            {mailingLive && (
              <Box mt={1}>
                <Button
                  size="small"
                  color="error"
                  onClick={() => void stop(false)}
                >
                  Отменить рассылку
                </Button>
              </Box>
            )}
          </Alert>
        )}
        {pendingData && (
          <Modal
            title="Подтвердите запуск рассылки"
            open
            onClose={() => setPendingData(null)}
            actions={[
              <Button key="cancel" onClick={() => setPendingData(null)}>
                Отмена
              </Button>,
              <Button
                key="confirm"
                color="error"
                variant="contained"
                onClick={() => {
                  const data = pendingData
                  setPendingData(null)
                  if (data) performSubmit(data)
                }}
              >
                Всё равно отправить
              </Button>,
            ]}
          >
            <Stack spacing={1}>
              {warnings.map((w, i) => (
                <Typography key={i} color="error">
                  {w}
                </Typography>
              ))}
              <Typography variant="body2" color="text.secondary">
                Получателей: {recipientCount}. Продолжить?
              </Typography>
            </Stack>
          </Modal>
        )}
        <Grid container spacing={1}>
          <Grid item xs={12} md={6} xl={4}>
            <Stack spacing={1}>
              <Name<IMailingFormData> required />
              <Type<IMailingFormData> />
              <DedupByUserId<IMailingFormData> />
              <ScheduleDate<IMailingFormData> />
              <Speed<IMailingFormData> recipientCount={recipientCount} />
              <Comment<IMailingFormData> />
            </Stack>
          </Grid>
          <Grid item xs={12} md={6} xl={4}>
            <Stack spacing={1}>
              <MethodSelectField<IMailingFormData> />
              <MailingBody<typeof method, IMailingFormData> />
            </Stack>
          </Grid>
          <Grid item xs={12} md={12} xl={4}>
            <MailingCredentials<IMailingFormData> />
          </Grid>
        </Grid>
        <Divider light sx={{ marginY: 2 }} />
        <Box display="flex" justifyContent="end" alignItems="center" gap={4}>
          <PreviewFab
            method={method}
            body={body}
            beforeOpen={handlePreviewBeforeOpen}
          />
          <Button
            disabled={isBusy || resubmitBlocked}
            type="submit"
            variant="contained"
          >
            Отправить&nbsp;{isBusy && <CircularProgress size={20} />}
          </Button>
        </Box>
      </FormContainer>
    </Stack>
  )
}
